/**
 * WEB-3739 доводка 1 — строка «Первые шаги» в низу бокового меню, по `FirstSteps`
 * эталона `design/cabinet-rescin/app.jsx` (стр. 475–490; место — `sections/
 * shell-x.jsx:152`: после «Поиска и ИИ», перед профилем; стили `.steps-row`,
 * `.steps-list` — `styles/cabinet-shell.css`).
 *
 * Кнопка `steps-row`: галочка, «Первые шаги», «N из M» (N — шаги с `done === true`,
 * M — число шагов ответа: пять, у своих платежей — три), стрелка. Нажатие
 * раскрывает вверх список `steps-list pop` (`steps-pop`): у пункта точка `off`
 * (сделан), `live` (первый не сделанный), `idle` (остальные); `done === null`
 * («проверка не ответила») — как не сделанный. Пункт ведёт в раздел, где шаг
 * делается (эталон `STEP_HASH`): воронка и публикация — `/content`, оплата —
 * вкладка платежей настроек проекта, домен — `/domains`, покупка — «Подписки»
 * (`/commerce`, пункт меню `store`). Закрытие — щелчок мимо и Esc (`useOutside`
 * кита). Шаг доступен с клавиатуры: `tabIndex` 0, Enter и пробел — как щелчок
 * (WEB-4074 №27; фокус — правило `.steps-list li:focus-visible`). Показывать ли строку (уровень проекта, `isNew === true`, не узкое меню)
 * решает `Sidebar.tsx`; ответ ручки — `useProjectFirstSteps` (WEB-3889).
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, useOutside } from '@/components/kit/ds';
import type { ProjectFirstSteps } from '@/api/firstSteps';
import { useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';

type Step = ProjectFirstSteps['steps'][number];

const STEP_LABEL = {
  funnel: 'firstStepFunnel',
  payments: 'firstStepPayments',
  domain: 'firstStepDomain',
  publish: 'firstStepPublish',
  purchase: 'firstStepPurchase',
} as const satisfies Record<Step['key'], keyof (typeof commonStrings)['en']>;

/**
 * Куда ведёт шаг (эталон `STEP_HASH`: content, settings, domains, content, store).
 * Одна карта и для шагов главной нового проекта (`NewProjectHome`, WEB-3744).
 */
export const STEP_PATH: Record<Step['key'], string> = {
  funnel: '/content',
  payments: '/project/settings?tab=stripe',
  domain: '/domains',
  publish: '/content',
  purchase: '/commerce',
};

export interface FirstStepsRowProps {
  steps: Step[];
}

export function FirstStepsRow({ steps }: FirstStepsRowProps) {
  const s = useStrings(commonStrings);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutside(ref, () => setOpen(false), open);
  const next = steps.findIndex((x) => x.done !== true);
  const n = steps.filter((x) => x.done === true).length;

  return (
    <div data-testid="first-steps" ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="steps-row"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        title={s.firstSteps}
      >
        <Icon name="check" size={16} />
        <span style={{ flex: 1, textAlign: 'left' }}>{s.firstSteps}</span>
        <span className="num" style={{ color: 'var(--fg-3)' }}>
          {s.firstStepsOf(n, steps.length)}
        </span>
        <Icon name="down" size={14} />
      </button>
      {open && (
        <ul
          className="steps-list pop"
          style={{ bottom: 'calc(100% + 6px)', top: 'auto', left: 0, right: 0 }}
          data-testid="steps-pop"
        >
          {steps.map((st, i) => {
            const done = st.done === true;
            const go = () => {
              setOpen(false);
              navigate(STEP_PATH[st.key]);
            };
            return (
              <li
                key={st.key}
                className={done ? 'done' : i === next ? 'next' : ''}
                tabIndex={0}
                onClick={go}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  go();
                }}
              >
                <span
                  className={
                    'dot ' + (done ? 'off' : i === next ? 'live' : 'idle')
                  }
                  aria-hidden="true"
                />
                {s[STEP_LABEL[st.key]]}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
